<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcCompanyStatement',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    oid: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getQueryParameters = (...args) => {
    return { client_id: props.oid };
  };

  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_columnDefaults = computed(() => ({
    sortable: false,
    filterable: true,
    resizable: true,
  }));
  const b_7315092382398562_columns = computed(() => [
    {
      field: 'transaction_date',
      name: $fn.value.pgettext('CompanyStatement', 'Date'),
      type: 'date',
    },
    {
      field: 'transaction_type',
      name: $fn.value.pgettext('CompanyStatement', 'Type'),
      width: '100px',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            conversion: 'StartCase',
            context: 'CompanyStatementType',
            filterInput: true,
          },
          {
            name: 'cellclass',
            value: (row) => ({
              'fw-bold': true,
              'bg-red-200': row.transaction_type === 'invoice',
              'bg-green-200': row.transaction_type === 'payment',
            }),
          },
        ],
      },
    },
    {
      field: 'transaction_number',
      name: $fn.value.pgettext('CompanyStatement', 'Number'),
      width: '120px',
      type: 'command',
      formatter: {
        name: 'link',
        href: '#',
      },
    },
    {
      field: 'amount',
      name: $fn.value.pgettext('CompanyStatement', 'Amount'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('CompanyStatement', 'Currency'),
    },
    {
      field: 'balance',
      name: $fn.value.pgettext('CompanyStatement', 'Balance'),
      type: 'number',
      formatter: 'number',
      noFooterTotals: true,
    },
  ]);

  const $data = computed(() => ({
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_columnDefaults: b_7315092382398562_columnDefaults.value,
    b_7315092382398562_columns: b_7315092382398562_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('browse_client_statement', {
      parameters: getQueryParameters,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_columnDefaults', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_columns', nodeId: '7315092382398562' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      ds,
      ux,
      b_7315092382398562_columnDefaults,
      b_7315092382398562_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );

  const act_7315092382398562_showModal_1dfn2lhz0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        modal: true,
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_7315092382398562_command_ia79vh0s6_getWhen = async (
    $event
  ) => {
    return (
      $event.data.column.id === 'transaction_number' &&
      $event.data.data.transaction_type === 'payment'
    );
  };
  const act_7315092382398562_showModal_f492okivf_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Invoice',
      props: {
        modal: true,
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_7315092382398562_command_s9l2ookxx_getWhen = async (
    $event
  ) => {
    return (
      $event.data.column.id === 'transaction_number' &&
      $event.data.data.transaction_type === 'invoice'
    );
  };

  const act_7315092382398562_showModal_1dfn2lhz0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7315092382398562_showModal_1dfn2lhz0_getActionArgs,
    });
  const inst_evh_7315092382398562_command_ia79vh0s6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_showModal_1dfn2lhz0,
      event: 'command',
      when: inst_evh_7315092382398562_command_ia79vh0s6_getWhen,
      displayName: 'showModal',
    });
  const act_7315092382398562_showModal_f492okivf =
    new actionTypes.ShowModalAction({
      actionArgs: act_7315092382398562_showModal_f492okivf_getActionArgs,
    });
  const inst_evh_7315092382398562_command_s9l2ookxx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_showModal_f492okivf,
      event: 'command',
      when: inst_evh_7315092382398562_command_s9l2ookxx_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_7315092382398562_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_7315092382398562_command_ia79vh0s6,
      inst_evh_7315092382398562_command_s9l2ookxx,
    ],
    event: 'command',
  });

  const actions = {
    inst_evh_7315092382398562_command_ia79vh0s6,
    inst_evh_7315092382398562_command_s9l2ookxx,
    inst_evh_7315092382398562_command,
  };

  const evh_7315092382398562_command_ia79vh0s6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_command_ia79vh0s6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_command_s9l2ookxx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_command_s9l2ookxx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_command = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <VueSlickgrid
    name="clientStatement"
    :headerTitle="pgettext('client|Statement', 'Statement')"
    :items="ds"
    checkbox-row-selection
    footer-totals
    export-to-csv
    :exportToCsvFilename="pgettext('filename', 'client-statement')"
    :columnDefaults="b_7315092382398562_columnDefaults"
    :columns="b_7315092382398562_columns"
    ref="grid"
    v-if="loaded"
    @command="evh_7315092382398562_command($event, {})"
  >
  </VueSlickgrid>
</template>
